/**
 * Ad preview modal (#6213) — Meta's OWN rendering of an ad, via the Graph previews API.
 *
 * The backend returns the ``<iframe src="https://business.facebook.com/ads/api/preview_iframe.php…">``
 * snippet Graph serves (live-verified shape); it embeds as-is — the content is served by Meta,
 * never proxied or rewritten. A format switcher mirrors Ads Manager's placements set.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/glitch-ui/spinner';
import { metaAdPreviewQueryOptions } from '@/lib/query-options';
import { META_AD_PREVIEW_FORMATS } from '@/lib/types';

export function MetaAdPreviewModal({ adId, adName, onClose }: { adId: number; adName: string; onClose: () => void }) {
  const [format, setFormat] = useState(META_AD_PREVIEW_FORMATS[0].id);
  const { data, isLoading, isError, error } = useQuery(metaAdPreviewQueryOptions(adId, format));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden sm:max-w-[640px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Ad Preview</DialogTitle>
          <p className="text-sm text-gray-600">How "{adName}" renders on Meta, straight from Meta.</p>
        </DialogHeader>
        <div className="flex gap-0 border-b border-gray-200">
          {META_AD_PREVIEW_FORMATS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setFormat(option.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                format === option.id
                  ? 'border-[#290BD9] text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
              <Spinner className="h-4 w-4" /> Loading Meta's preview…
            </div>
          ) : isError || !data ? (
            <div className="flex items-start gap-3 rounded-lg border-l-4 border-[#FCA5A5] bg-[#FEE2E2] p-4 text-[#B91C1C]">
              <AlertCircle className="mt-0.5 h-5 w-5" />
              <p className="text-sm">{error instanceof Error ? error.message : 'The preview could not be loaded.'}</p>
            </div>
          ) : (
            // Meta's snippet is a single business.facebook.com iframe (live-verified) — rendered
            // as-is in a centered container; the iframe carries its own dimensions per format.
            <div className="flex justify-center" dangerouslySetInnerHTML={{ __html: data.body }} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
